/**
 * DocumentHealthCard — Phase 36 Batch AA.5 Feature 1 Batch B.
 *
 * Aggregate "Document Health" score that lives on the AI Analysis tab.
 * Average of the AI ratings across every `complete` document for this
 * deal. Hidden when no documents have been uploaded yet, with a quick
 * link nudging the user toward the Documents tab.
 *
 * This is a NEW, independent score — it does NOT factor into the
 * existing Deal Score by design (per the spec) so the two signals
 * stay legible.
 *
 * Uses the shared `api` instance so the 401 interceptor catches the
 * first-load auth race.
 */
import { useEffect, useState } from "react";
import { FileText, Sparkles, ArrowRight } from "lucide-react";
import api from "@/lib/api";

function avgRating(docs) {
  const rated = docs
    .filter((d) => d.analysis_status === "complete" && d.ai_analysis?.rating != null)
    .map((d) => d.ai_analysis.rating);
  if (rated.length === 0) return null;
  return rated.reduce((a, b) => a + b, 0) / rated.length;
}

function healthTone(avg) {
  if (avg == null) return "neutral";
  if (avg >= 7) return "strong";
  if (avg >= 4) return "caution";
  return "weak";
}

const TONE_STYLES = {
  strong: {
    card: "border-emerald-200 bg-gradient-to-br from-emerald-50/60 via-white to-white",
    pill: "bg-emerald-600 text-white",
    number: "text-emerald-700",
  },
  caution: {
    card: "border-amber-200 bg-gradient-to-br from-amber-50/60 via-white to-white",
    pill: "bg-amber-600 text-white",
    number: "text-amber-800",
  },
  weak: {
    card: "border-rose-200 bg-gradient-to-br from-rose-50/60 via-white to-white",
    pill: "bg-rose-600 text-white",
    number: "text-rose-700",
  },
  neutral: {
    card: "border-slate-200 bg-white",
    pill: "bg-slate-500 text-white",
    number: "text-slate-700",
  },
};


export default function DocumentHealthCard({ dealId, onJumpToDocs }) {
  const [docs, setDocs] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!dealId) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/deals/${dealId}/documents`);
        if (alive) setDocs(Array.isArray(data) ? data : []);
      } catch {
        // silent — empty state is the safest default
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, [dealId]);

  if (!loaded) return null;

  const completeCount = docs.filter((d) => d.analysis_status === "complete").length;

  // No completed analyses yet → nudge card pointing at the Documents
  // tab. We deliberately stay quiet until the user has at least
  // uploaded SOMETHING — surfacing a zero-state Document Health on
  // every deal would dilute the signal.
  if (completeCount === 0) {
    return (
      <section
        data-testid="doc-health-empty"
        className="rounded-2xl border border-dashed border-slate-300 bg-white p-5 shadow-sm"
      >
        <div className="flex items-start gap-4">
          <span className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-slate-100 text-slate-600">
            <FileText className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold tracking-tight text-slate-900">
              Document Health — not analyzed yet
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              Upload an inspection, HOA financials, or any deal doc and the
              AI will score it. Their average becomes your Document Health
              rating — independent from the Deal Score above.
            </p>
            {onJumpToDocs ? (
              <button
                type="button"
                onClick={onJumpToDocs}
                data-testid="doc-health-cta"
                className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 hover:text-indigo-700"
              >
                Upload documents <ArrowRight className="h-4 w-4" />
              </button>
            ) : null}
          </div>
        </div>
      </section>
    );
  }

  const avg = avgRating(docs);
  const tone = healthTone(avg);
  const t = TONE_STYLES[tone];

  return (
    <section
      data-testid="doc-health-card"
      data-tone={tone}
      data-doc-count={completeCount}
      data-avg-rating={avg != null ? avg.toFixed(2) : ""}
      className={`rounded-2xl border p-5 shadow-sm sm:p-6 ${t.card}`}
    >
      <div className="flex items-start gap-4">
        <span className={`flex h-10 w-10 flex-none items-center justify-center rounded-xl ${t.pill} shadow-sm`}>
          <Sparkles className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-3">
            <h3 className="text-base font-bold tracking-tight text-slate-900 sm:text-lg">
              Document Health
            </h3>
            <span className="text-xs font-medium text-slate-500">
              Independent from Deal Score · {completeCount} analyzed
            </span>
          </div>
          <div className="mt-2 flex items-baseline gap-3">
            <span
              data-testid="doc-health-score"
              className={`text-4xl font-bold tabular-nums ${t.number}`}
            >
              {avg != null ? avg.toFixed(1) : "—"}
            </span>
            <span className="text-sm font-medium text-slate-500">/ 10</span>
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Average AI rating across {completeCount} complete document
            {completeCount === 1 ? "" : "s"}.
            {docs.length > completeCount ? (
              <> {docs.length - completeCount} still analyzing.</>
            ) : null}
          </p>
        </div>
      </div>
    </section>
  );
}
